import { v4 as uuidv4, v7 as uuidv7 } from 'uuid';
import {
    replacePlanInstructions,
    setSelectionAfterPaste,
    planStructureChanged,
} from './automationStudioNewSlice';
import { showSnackbar } from '../snakbar/snackbarSlice';
import { TOASTS } from '../../utils/automation/copyPasteConstants';
import { reindexInstructionsGlobally, createDefaultInstructionWithSteps } from './instructionMutations';

function resolveTarget({ instructions, position, anchorStepId, anchorInstructionId }) {
    if (position === 'above' || position === 'below') {
        for (const inst of instructions) {
            const idx = (inst.aiSteps ?? []).findIndex((s) => s.id === anchorStepId);
            if (idx !== -1) {
                return {
                    instructionId: inst.id,
                    insertIndex: position === 'above' ? idx : idx + 1,
                };
            }
        }
        return null;
    }
    if (anchorInstructionId) {
        const target = instructions.find((i) => i.id === anchorInstructionId);
        if (!target) return null;
        return { instructionId: target.id, insertIndex: (target.aiSteps ?? []).length };
    }
    if (instructions.length === 0) return null;
    const last = instructions[instructions.length - 1];
    return { instructionId: last.id, insertIndex: (last.aiSteps ?? []).length };
}

function materialize(envelopeSteps, targetInstructionId) {
    const now = new Date().toISOString();
    return envelopeSteps.map((s) => ({
        id: uuidv4(),
        stepId: uuidv7(),
        instructionId: targetInstructionId,
        status: 'pending',
        timestamp: now,
        tool: s.tool,
        args: s.args,
        stepDescription: s.stepDescription,
        description: s.stepDescription,
        executionMode: s.executionMode,
        executionStrategies: s.executionStrategies,
        customStep: s.customStep,
        extractedData: { stepDescription: s.stepDescription },
    }));
}

export function pasteStepsNew({
    envelope,
    position,
    anchorStepId,
    anchorInstructionId = null,
    getState,
    dispatch,
}) {
    const { instructions } = getState();
    const target = resolveTarget({ instructions, position, anchorStepId, anchorInstructionId });

    let updatedInstructions;
    let newSteps;
    let createdDefaultInstruction = false;

    if (target === null) {
        if (position === 'append-end' && !anchorInstructionId && instructions.length === 0) {
            const newInstructionId = uuidv4();
            newSteps = materialize(envelope.steps, newInstructionId);
            updatedInstructions = [{ id: newInstructionId, text: '', aiSteps: newSteps }];
            createdDefaultInstruction = true;
        } else {
            return;
        }
    } else {
        newSteps = materialize(envelope.steps, target.instructionId);
        updatedInstructions = instructions.map((inst) => {
            if (inst.id !== target.instructionId) return inst;
            const existing = inst.aiSteps ?? [];
            return {
                ...inst,
                aiSteps: [
                    ...existing.slice(0, target.insertIndex),
                    ...newSteps,
                    ...existing.slice(target.insertIndex),
                ],
            };
        });
    }

    const reindexed = reindexInstructionsGlobally(updatedInstructions);
    const pastedIds = newSteps.map((s) => s.id);

    dispatch(replacePlanInstructions(reindexed));
    dispatch(setSelectionAfterPaste(pastedIds));
    dispatch(planStructureChanged());
    dispatch(
        showSnackbar({
            message: createdDefaultInstruction
                ? TOASTS.PASTE_OK_WITH_DEFAULT_INSTRUCTION(envelope.steps.length)
                : TOASTS.PASTE_OK(envelope.steps.length),
            severity: 'success',
            duration: 4000,
        })
    );
}
